// src/utils/roleUtils.js

import { fetchAuthSession } from 'aws-amplify/auth';

// Roles permitidos en el sistema
export const ALLOWED_ROLES = [
  'admin_tenant',
  'lider_administrativo',
  'admin_marca',
  'admin_retailer',
  'supervisor_zona',
  'lider_promotoria'
];

// Función para obtener los grupos de Cognito del usuario
export const getCognitoGroups = async () => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    
    if (!idToken) {
      // console.warn('⚠️ [ROLES] No se pudo obtener el ID token');
      return [];
    }
    
    const payload = idToken.payload || {};
    const groups = payload["cognito:groups"] || [];
    
    // console.log('🔍 [ROLES] Grupos de Cognito obtenidos:', groups);
    return groups;
  } catch (error) {
    // console.error('❌ [ROLES] Error al obtener grupos de Cognito:', error);
    return [];
  }
};

// Función para extraer el rol del usuario desde los grupos de Cognito
export const extractUserRole = (groups, tenantId) => {
  if (!groups || !Array.isArray(groups) || groups.length === 0) {
    // console.log('⚠️ [ROLES] No hay grupos para procesar');
    return null;
  }
  
  if (!tenantId) {
    // console.log('⚠️ [ROLES] No hay tenant_id disponible');
    return null;
  }
  
  // console.log('🔍 [ROLES] Procesando grupos:', groups);
  // console.log('🔍 [ROLES] Tenant ID:', tenantId);
  
  // Intentar dos métodos de búsqueda:
  // 1. Búsqueda exacta (tenant_id#role)
  // 2. Búsqueda parcial (role contenido en el grupo)
  
  for (const role of ALLOWED_ROLES) {
    // Método 1: Búsqueda exacta
    const exactGroupName = `${tenantId}#${role}`;
    if (groups.includes(exactGroupName)) {
      // console.log('✅ [ROLES] Rol encontrado (método exacto):', role);
      return role;
    }
    
    // Método 2: Búsqueda parcial
    const foundGroup = groups.find(group => group.includes(role));
    if (foundGroup) {
      // console.log('✅ [ROLES] Rol encontrado (método parcial):', role, 'en grupo:', foundGroup);
      return role;
    }
  }
  
  // console.log('❌ [ROLES] No se encontró ningún rol válido');
  // console.log('❌ [ROLES] Grupos disponibles:', groups);
  // console.log('❌ [ROLES] Roles buscados:', ALLOWED_ROLES);
  
  return null;
};

// Función para obtener el tenant_id de forma robusta
export const getTenantId = async () => {
  // Intentar obtener desde localStorage primero
  let tenantId = localStorage.getItem('tenant_id');
  
  if (tenantId) {
    // console.log('✅ [ROLES] Tenant ID obtenido desde localStorage:', tenantId);
    return tenantId;
  }
  
  // Si no está en localStorage, intentar obtenerlo desde el token de Cognito
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    
    if (idToken) {
      const payload = idToken.payload || {};
      tenantId = payload.tenant_id;
      
      if (tenantId) {
        // console.log('✅ [ROLES] Tenant ID obtenido desde token:', tenantId);
        // Guardarlo en localStorage para usos futuros
        localStorage.setItem('tenant_id', tenantId);
        return tenantId;
      }
    }
  } catch (error) {
    // console.error('❌ [ROLES] Error al obtener tenant_id desde token:', error);
  }
  
  // console.log('❌ [ROLES] No se pudo obtener tenant_id');
  return null;
};

// Función principal para obtener el rol del usuario
export const getUserRole = async () => {
  try {
    // console.log('🔄 [ROLES] Iniciando obtención de rol del usuario...');
    
    // Obtener tenant_id
    const tenantId = await getTenantId();
    if (!tenantId) {
      // console.log('❌ [ROLES] No se pudo obtener tenant_id');
      return null;
    }
    
    // Obtener grupos de Cognito
    const groups = await getCognitoGroups();
    if (!groups || groups.length === 0) {
      // console.log('❌ [ROLES] No hay grupos de Cognito disponibles');
      return null;
    }
    
    // Extraer el rol
    const role = extractUserRole(groups, tenantId);
    
    if (role) {
      // console.log('✅ [ROLES] Rol del usuario detectado:', role);
      // Guardar el rol en localStorage
      localStorage.setItem('user_role', role);
      localStorage.setItem('cognito_groups', JSON.stringify(groups));
      return role;
    }
    
    // console.log('❌ [ROLES] No se pudo determinar el rol del usuario');
    return null;
  } catch (error) {
    // console.error('💥 [ROLES] Error al obtener rol del usuario:', error);
    return null;
  }
};

// Función para verificar si un usuario tiene acceso a un recurso
export const hasAccess = async (requiredRoles = ALLOWED_ROLES) => {
  try {
    const userRole = await getUserRole();
    
    if (!userRole) {
      // console.log('❌ [ACCESS] Usuario sin rol válido');
      return false;
    }
    
    const hasValidRole = requiredRoles.includes(userRole);
    // console.log(`🔐 [ACCESS] Verificando acceso - Rol: ${userRole}, Requerido: ${requiredRoles.join(', ')}, Acceso: ${hasValidRole}`);
    
    return hasValidRole;
  } catch (error) {
    // console.error('💥 [ACCESS] Error al verificar acceso:', error);
    return false;
  }
};

// Función para verificar si el usuario es admin_tenant
export const isAdminTenant = async () => {
  const userRole = await getUserRole();
  const isAdmin = userRole === 'admin_tenant';
  // console.log(`👑 [ADMIN] ¿Es admin_tenant? ${isAdmin} (rol: ${userRole})`);
  return isAdmin;
};

// Función para limpiar datos de rol (útil para debugging)
export const clearRoleData = () => {
  localStorage.removeItem('user_role');
  localStorage.removeItem('cognito_groups');
  // console.log('🧹 [ROLES] Datos de rol limpiados');
};

// Función para convertir tenant_id a título capitalizado
export const getTenantIdTitleCase = (tenantId) => {
  if (!tenantId || typeof tenantId !== 'string') {
    return '';
  }
  
  return tenantId
    .toLowerCase()
    .split(/[\s_-]+/) // Split por espacios, guiones bajos o guiones
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}; 